import { useMemo, type Dispatch, type SetStateAction } from 'react';
import { Grid, InputAdornment, TextField, Typography } from '@mui/material';
import { toast } from 'sonner';

import { usePatchLoanRefinanceFieldsMutation } from '@/apis/extended/loanApplicationApi';
import type {
  LoanApplication,
  LoanRefinanceFieldsSnapshot,
} from '@/types/LoanApplicationType';
import { parseLoanPatchError } from './loanSectionForm';
import { LoanSectionFormFooter } from './LoanSectionFormFooter';
import { MoneyField } from './MoneyField';
import { useLoanSectionForm } from './useLoanSectionForm';

type Props = {
  application: LoanApplication;
  readOnly?: boolean;
};

const emptyRefi = (): LoanRefinanceFieldsSnapshot => ({
  currentLoanBalance: null,
  currentRatePct: null,
  currentTermYears: null,
  currentMonthlyPayment: null,
  requestedLoanAmount: null,
  newTermYears: null,
  cashOutAmount: null,
  estimatedPayoff: null,
});

function numField(
  key: keyof LoanRefinanceFieldsSnapshot,
  local: LoanRefinanceFieldsSnapshot,
  setLocal: Dispatch<SetStateAction<LoanRefinanceFieldsSnapshot>>,
  disabled: boolean,
  label: string,
  adornment?: 'dollar' | 'percent'
) {
  return (
    <TextField
      fullWidth
      label={label}
      type="number"
      disabled={disabled}
      value={local[key] ?? ''}
      onChange={(e) =>
        setLocal((prev) => ({
          ...prev,
          [key]: e.target.value === '' ? null : Number(e.target.value),
        }))
      }
      InputProps={
        adornment === 'dollar'
          ? {
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }
          : adornment === 'percent'
          ? { endAdornment: <InputAdornment position="end">%</InputAdornment> }
          : undefined
      }
    />
  );
}

export function RefinanceFieldsSection({ application, readOnly }: Props) {
  const [patch, { isLoading }] = usePatchLoanRefinanceFieldsMutation();

  const initial = useMemo(
    () => ({ ...emptyRefi(), ...(application.refinanceFields || {}) }),
    [application._id, application.refinanceFields, application.updatedAt]
  );

  const {
    draft: local,
    setDraft: setLocal,
    isDirty,
    markSaved,
    resetDraft,
    readOnly: formReadOnly,
  } = useLoanSectionForm({
    sectionKey: 'refinance-terms',
    applicationId: application._id,
    applicationUpdatedAt: application.updatedAt,
    initialValue: initial,
    readOnly,
  });

  const saveSection = async () => {
    if (formReadOnly) return;
    try {
      await patch({ id: application._id, refinanceFields: local }).unwrap();
      markSaved(local);
      toast.success('Saved');
    } catch (e: unknown) {
      toast.error(parseLoanPatchError(e));
    }
  };

  const disabled = formReadOnly || isLoading;

  const money = (
    key: keyof LoanRefinanceFieldsSnapshot,
    label: string,
    helperText?: string
  ) => (
    <MoneyField
      label={label}
      value={local[key] as number | null}
      disabled={disabled}
      helperText={helperText}
      onChange={(v) => setLocal((prev) => ({ ...prev, [key]: v }))}
    />
  );

  return (
    <div>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The loan you have now. We filled in what you told us when you added your
        home — check it against your latest mortgage statement.
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          {money(
            'currentLoanBalance',
            'Current loan balance',
            'Also used for the property’s mortgage balance and your Debts — enter it once'
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          {money(
            'estimatedPayoff',
            'Payoff amount (if you have a payoff statement)',
            'Optional — usually a little more than the balance'
          )}
        </Grid>
        <Grid item xs={12} sm={4}>
          {numField(
            'currentRatePct',
            local,
            setLocal,
            disabled,
            'Current rate',
            'percent'
          )}
        </Grid>
        <Grid item xs={12} sm={4}>
          {numField(
            'currentTermYears',
            local,
            setLocal,
            disabled,
            'Original term (years)'
          )}
        </Grid>
        <Grid item xs={12} sm={4}>
          {money('currentMonthlyPayment', 'Current monthly payment')}
        </Grid>
      </Grid>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 2 }}
      >
        Cash out, the new loan amount and term are in “Your loan”.
      </Typography>
      <LoanSectionFormFooter
        readOnly={formReadOnly}
        isDirty={isDirty}
        isSaving={isLoading}
        saveLabel="Save current mortgage"
        onSave={saveSection}
        onCancel={resetDraft}
      />
    </div>
  );
}
